import { IGroupCatalogue } from 'fitbeat.gym-core';
import { IGroupTemplate, ILeaderboardConfig } from 'fitbeat.models';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { History } from 'history';
import IntlMessages from '../util/IntlMessages';
import { IAppStore } from '../rootReducer';
import getLogger from '../util/logging';
import { Loading } from '../shared/loading';
import '../styles/leaderboards/groupCatalogue.scss';
import CatalogueActionBar from './components/catalogue/CatalogueActionBar';
import CatalogueControls from './components/catalogue/CatalogueControls';
import LoadError from './components/catalogue/LoadError';
import TemplateSection from './components/catalogue/TemplateSection';
import { isTemplateAlreadyCreated } from './components/helpers';
import {
  createGroupsFromTemplates,
  getGroupCatalogue,
  getLeaderboardConfigs,
} from './leaderboardsSlice';

interface IProps {
  history: History;
  selectedGymName: string;
  leaderboardConfigs: ILeaderboardConfig[];
  groupCatalogue: IGroupCatalogue;
  isFetchingGroupCatalogue: boolean;
  loadError: string | null;
  loadGroupCatalogue: (gym: string) => void;
  loadLeaderboardConfigs: (gym: string) => void;
  createGroups: (gym: string, templateIds: string[], startDate: string) => Promise<void>;
}

interface IState {
  startDate: string;
  selectedTemplateIds: string[];
  isSaving: boolean;
  saveError: string;
}

class GroupCatalogue extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      startDate: moment().format('YYYY-MM-DD'),
      selectedTemplateIds: [],
      isSaving: false,
      saveError: '',
    };
  }

  public componentDidMount() {
    this.load();
  }

  public render() {
    return (
      <div>
        <form onSubmit={this.handleSubmit}>
          <CatalogueActionBar
            selectedCount={this.orderedSelection().length}
            isSaving={this.state.isSaving}
            saveError={this.state.saveError}
            onDiscard={this.discard}
          />
          <CatalogueControls
            gymName={this.props.selectedGymName}
            startDate={this.state.startDate}
            isSaving={this.state.isSaving}
            onStartDateChange={(startDate) => this.setState({ startDate })}
          />
          {this.renderCatalogue()}
        </form>
      </div>
    );
  }

  private load = () => {
    this.props.loadGroupCatalogue(this.props.selectedGymName);
    this.props.loadLeaderboardConfigs(this.props.selectedGymName);
  };

  private discard = () => {
    this.props.history.push('/app/leaderboards');
  };

  private isAlreadyCreated = (template: IGroupTemplate): boolean =>
    isTemplateAlreadyCreated(template, this.props.leaderboardConfigs, this.props.selectedGymName);

  private toggleTemplate = (templateId: string) => {
    this.setState((state) => ({
      selectedTemplateIds: state.selectedTemplateIds.includes(templateId)
        ? state.selectedTemplateIds.filter((id) => id !== templateId)
        : [...state.selectedTemplateIds, templateId],
    }));
  };

  private allTemplates(): IGroupTemplate[] {
    return [...this.props.groupCatalogue.exercise, ...this.props.groupCatalogue.interest];
  }

  private orderedSelection(): string[] {
    return this.allTemplates()
      .filter((template) => this.state.selectedTemplateIds.includes(template._id) && !this.isAlreadyCreated(template))
      .map((template) => template._id);
  }

  private handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const logger = getLogger('groups:catalogue');
    this.setState({ isSaving: true, saveError: '' });
    try {
      await this.props.createGroups(this.props.selectedGymName, this.orderedSelection(), this.state.startDate);
      this.props.loadLeaderboardConfigs(this.props.selectedGymName);
      this.props.history.push('/app/leaderboards');
    } catch (error) {
      logger.error(error, 'Failed to create groups from templates');
      this.setState({
        isSaving: false,
        saveError: (error as Error)?.message || 'Failed to create groups.',
      });
    }
  };

  private renderSection(titleId: string, templates: IGroupTemplate[]) {
    return (
      <TemplateSection
        titleId={titleId}
        templates={templates}
        selectedTemplateIds={this.state.selectedTemplateIds}
        isSaving={this.state.isSaving}
        isAlreadyCreated={this.isAlreadyCreated}
        onToggle={this.toggleTemplate}
      />
    );
  }

  private renderCatalogue() {
    const { exercise, interest } = this.props.groupCatalogue;
    if (this.props.isFetchingGroupCatalogue) {
      return <div className='loader-container'><Loading /></div>;
    }
    if (this.props.loadError) {
      return <LoadError message={this.props.loadError} onRetry={this.load} />;
    }
    return (
      <>
        {exercise.length === 0 && interest.length === 0 && (
          <p><IntlMessages id='groups.catalogue.empty' /></p>
        )}
        {this.renderSection('groups.catalogue.exercise', exercise)}
        {this.renderSection('groups.catalogue.interest', interest)}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  loadGroupCatalogue: (gym: string) => dispatch(getGroupCatalogue(gym)),
  loadLeaderboardConfigs: (gym: string) => dispatch(getLeaderboardConfigs(gym)),
  createGroups: async (gym: string, templateIds: string[], startDate: string) => {
    await dispatch(createGroupsFromTemplates({
      gym,
      request: { templateIds, startDate },
    })).unwrap();
  },
});

const mapStateToProps = (state: IAppStore) => {
  const { leaderboards, adminSettings } = state;
  return {
    selectedGymName: adminSettings.data.selectedGymName,
    leaderboardConfigs: leaderboards.data.leaderboardConfigs,
    groupCatalogue: leaderboards.data.groupCatalogue,
    isFetchingGroupCatalogue: leaderboards.view.isFetchingGroupCatalogue,
    loadError: leaderboards.view.groupCatalogueErrorMessage || leaderboards.view.errorMessage,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(GroupCatalogue));
